import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { 
  OrderPdfData, 
  PdfGenerationOptions, 
  Question, 
  FormResponse 
} from '../models/order-pdf.model';

@Injectable({
  providedIn: 'root'
})
export class OrderPdfGeneratorService {
  private readonly DEFAULT_OPTIONS: PdfGenerationOptions = {
    includeImages: true,
    pageOrientation: 'portrait',
    fontSize: {
      title: 18,
      sectionHeader: 14,
      label: 11,
      value: 10
    },
    colors: {
      primary: '#2c3e50',
      secondary: '#34495e',
      text: '#2c3e50',
      border: '#bdc3c7'
    },
    margins: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20
    }
  };

  private doc!: jsPDF;
  private currentY: number = 0;
  private options!: PdfGenerationOptions;
  private pageWidth: number = 0;
  private pageHeight: number = 0;
  private contentWidth: number = 0;

  constructor() {}

  async generateOrderPdf(orderData: OrderPdfData, options: Partial<PdfGenerationOptions> = {}): Promise<Blob> {
    console.log('📄 Starting PDF generation...');
    
    // Log de imágenes encontradas
    if (orderData.formResponses) {
      let imageCount = 0;
      orderData.formResponses.forEach((form) => {
        if (form.questions) {
          form.questions.forEach((q) => {
            if (q.image) imageCount++;
            if (q.type === 'multipleChoice' && q.responses) {
              q.responses.forEach((r) => {
                if (r['image']) imageCount++;
              });
            }
          });
        }
      });
      console.log(`� Found ${imageCount} images to process`);
    }
    
    this.options = { ...this.DEFAULT_OPTIONS, ...options };
    this.initializeDocument();
    
    this.addHeader(orderData);
    this.addGeneralInformation(orderData);
    
    if (orderData.formResponses && orderData.formResponses.length > 0) {
      await this.addFormResponsesSection(orderData.formResponses);
    }
    
    console.log('✅ PDF generation completed');
    return this.doc.output('blob');
  }

  async downloadOrderPdf(orderData: OrderPdfData, filename?: string, options: Partial<PdfGenerationOptions> = {}): Promise<void> {
    const blob = await this.generateOrderPdf(orderData, options);
    const defaultFilename = `order_${orderData.identifier || orderData.orderId}_${Date.now()}.pdf`;
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename || defaultFilename;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private initializeDocument(): void {
    this.doc = new jsPDF({
      orientation: this.options.pageOrientation,
      unit: 'mm',
      format: 'a4'
    });

    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - this.options.margins!.left - this.options.margins!.right;
    this.currentY = this.options.margins!.top;
  }

  private addHeader(orderData: OrderPdfData): void {
    this.doc.setTextColor(this.options.colors!.primary);
    
    // Nombre del paciente en negrita con tamaño grande
    this.doc.setFontSize(this.options.fontSize!.title);
    this.doc.setFont('helvetica', 'bold');
    const patientName = `${orderData.patientData.name} ${orderData.patientData.surname}`;
    this.doc.text(patientName, this.options.margins!.left, this.currentY);
    
    // Fecha en el extremo derecho con tamaño más pequeño
    if (orderData.createdAt) {
      this.doc.setFontSize(this.options.fontSize!.label);
      const dateText = this.formatDate(orderData.createdAt);
      const dateWidth = this.doc.getTextWidth(dateText);
      this.doc.text(dateText, this.pageWidth - this.options.margins!.right - dateWidth, this.currentY);
    }
    
    this.currentY += 7;
    
    // Order ID debajo en pequeño
    this.doc.setFontSize(this.options.fontSize!.value);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Order ID: ${orderData.orderId}`, this.options.margins!.left, this.currentY);
    
    this.currentY += 10;
  }

  private addGeneralInformation(orderData: OrderPdfData): void {
    this.checkPageBreak(60);
    
    const patientFields = [
      { label: 'Birth Date', value: orderData.patientData.birthDate },
      { label: 'Email', value: orderData.patientData.email },
      { label: 'Sex', value: orderData.patientData.sex },
      { label: 'Shoe Size', value: orderData.patientData.shoeSize },
      { label: 'Weight', value: orderData.patientData.weight }
    ];
    
    // Agregar clínica si existe
    if (orderData.clinic) {
      patientFields.push({ label: 'Clinic', value: orderData.clinic.name });
    }
    
    // Mostrar campos en dos columnas para aprovechar el espacio
    this.addFieldsInColumns(patientFields);
    
    this.currentY += 8;
  }


   // Muestra campos en dos columnas para optimizar el espacio
  private addFieldsInColumns(fields: Array<{ label: string; value: any }>): void {
    const validFields = fields.filter(f => 
      f.value !== undefined && f.value !== null && f.value !== ''
    );
    
    const columnWidth = (this.contentWidth - 10) / 2;
    const leftX = this.options.margins!.left;
    const rightX = this.options.margins!.left + columnWidth + 10;
    
    for (let i = 0; i < validFields.length; i += 2) {
      this.checkPageBreak(8);
      
      // Campo izquierdo
      const leftField = validFields[i];
      this.addFieldInColumn(leftField.label, leftField.value, leftX, columnWidth);
      
      // Campo derecho (si existe)
      if (i + 1 < validFields.length) {
        const rightField = validFields[i + 1];
        const savedY = this.currentY;
        this.addFieldInColumn(rightField.label, rightField.value, rightX, columnWidth);
        // Asegurar que ambas columnas avancen igual
        if (this.currentY < savedY + 5) {
          this.currentY = savedY + 5;
        }
      } else {
        this.currentY += 5;
      }
    }
  }

  private addFieldInColumn(label: string, value: any, xPosition: number, maxWidth: number): void {
    this.doc.setFontSize(this.options.fontSize!.label);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.options.colors!.secondary);
    
    const labelText = `${label}: `;
    const labelWidth = this.doc.getTextWidth(labelText);
    
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.options.colors!.text);
    
    const valueStr = String(value);
    const availableWidth = maxWidth - labelWidth;
    
    // Si el valor cabe en una línea
    if (this.doc.getTextWidth(valueStr) <= availableWidth) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(labelText, xPosition, this.currentY);
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(valueStr, xPosition + labelWidth, this.currentY);
    } else {
      // Si el valor es muy largo, ponerlo en la siguiente línea
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(labelText, xPosition, this.currentY);
      this.currentY += 5;
      
      this.doc.setFont('helvetica', 'normal');
      const lines = this.doc.splitTextToSize(valueStr, maxWidth);
      lines.forEach((line: string) => {
        this.doc.text(line, xPosition, this.currentY);
        this.currentY += 5;
      });
      this.currentY -= 5; // Ajustar para la siguiente columna
    }
  }

  private async addFormResponsesSection(formResponses: FormResponse[]): Promise<void> {
    for (const form of formResponses) {
      this.checkPageBreak(40);
      
      this.addSectionHeader(form.formTitle);
      
      if (form.questions && form.questions.length > 0) {
        await this.addQuestionsInColumns(form.questions);
      }
      
      this.currentY += 5;
    }
  }

  private async addQuestionsInColumns(questions: Question[]): Promise<void> {
    const columnWidth = (this.contentWidth - 10) / 2;
    const leftX = this.options.margins!.left;
    const rightX = this.options.margins!.left + columnWidth + 10;
    
    let i = 0;
    while (i < questions.length) {
      const leftQuestion = questions[i];
      const rightQuestion = i + 1 < questions.length ? questions[i + 1] : null;
      
      // Verificar si ambas preguntas son simples (pueden ir en columnas)
      const leftIsSimple = this.isSimpleQuestion(leftQuestion);
      const rightIsSimple = rightQuestion ? this.isSimpleQuestion(rightQuestion) : false;
      
      if (leftIsSimple && rightIsSimple && rightQuestion) {
        // Ambas son simples, mostrar en dos columnas
        this.checkPageBreak(25);
        
        const startY = this.currentY;
        
        // Columna izquierda
        await this.addQuestionInColumn(leftQuestion, i + 1, leftX, columnWidth);
        const leftEndY = this.currentY;
        
        // Columna derecha
        this.currentY = startY;
        await this.addQuestionInColumn(rightQuestion, i + 2, rightX, columnWidth);
        const rightEndY = this.currentY;
        
        // Avanzar al final de la columna más larga
        this.currentY = Math.max(leftEndY, rightEndY) + 3;
        
        i += 2;
      } else {
        // Al menos una es compleja, mostrar en ancho completo
        await this.addQuestion(leftQuestion, i + 1);
        i += 1;
      }
    }
  }

  private isSimpleQuestion(question: Question): boolean {
    // Preguntas simples: dropdown, inputText con respuestas cortas
    if (question.type === 'dropdown' || question.type === 'inputText') {
      const response = question.response || '';
      return String(response).length < 50;
    }
    
    // multipleChoice con pocas opciones (1-2) - AHORA INCLUYE LAS QUE TIENEN IMÁGENES
    if (question.type === 'multipleChoice') {
      if (question.responses && question.responses.length <= 2) {
        // Verificar que los títulos sean cortos
        const totalLength = question.responses.reduce((sum, r) => sum + (r.title?.length || 0), 0);
        return totalLength < 60;
      }
    }
    
    // longText solo si es muy corto
    if (question.type === 'longText') {
      const response = question.response || '';
      return String(response).length < 40;
    }
    
    return false;
  }

  private async addQuestionInColumn(question: Question, index: number, xPosition: number, maxWidth: number): Promise<void> {
    // Título de la pregunta
    this.doc.setFontSize(this.options.fontSize!.label);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.options.colors!.secondary);
    
    const questionTitle = `${index}. ${question.title}`;
    const titleLines = this.doc.splitTextToSize(questionTitle, maxWidth);
    
    titleLines.forEach((line: string) => {
      this.doc.text(line, xPosition, this.currentY);
      this.currentY += 5;
    });
    
    // Respuesta
    this.doc.setFontSize(this.options.fontSize!.value);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.options.colors!.text);
    
    // Manejar diferentes tipos de respuestas
    if (question.type === 'multipleChoice' && question.responses && question.responses.length > 0) {
      for (const resp of question.responses) {
        const respLine = this.doc.splitTextToSize(`• ${resp.title}`, maxWidth - 5);
        respLine.forEach((line: string) => {
          this.doc.text(line, xPosition + 3, this.currentY);
          this.currentY += 5;
        });
        
        // Si la respuesta tiene una imagen, agregarla
        let imageInfo: { x: number; width: number } | null = null;
        if (this.options.includeImages && resp['image'] && resp['image'].trim() !== '') {
          this.currentY += 2;
          imageInfo = await this.addImageToDocument(resp['image'], xPosition + 3);
        }
        
        // Si hay información adicional en responses (como "Add", "1.5mm, 3mm", etc.)
        if (resp['responses'] && resp['responses'] !== '') {
          this.doc.setFont('helvetica', 'italic');
          this.doc.setFontSize(this.options.fontSize!.value - 1);
          const text = resp['responses'];
          const textWidth = this.doc.getTextWidth(text);
          
          // Centrar debajo de la imagen si existe
          let centeredX: number;
          if (imageInfo) {
            centeredX = imageInfo.x + (imageInfo.width - textWidth) / 2;
          } else {
            centeredX = xPosition + (maxWidth - textWidth) / 2;
          }
          
          this.doc.text(text, centeredX, this.currentY);
          this.currentY += 4;
          this.doc.setFontSize(this.options.fontSize!.value);
          this.doc.setFont('helvetica', 'normal');
        }
      }
    } else if (question.response) {
      const responseLines = this.doc.splitTextToSize(String(question.response), maxWidth - 5);
      responseLines.forEach((line: string) => {
        this.doc.text(line, xPosition + 3, this.currentY);
        this.currentY += 5;
      });
    }
  }

  private async addQuestion(question: Question, index: number): Promise<void> {
    this.checkPageBreak(30);
    
    // Título de la pregunta
    this.doc.setFontSize(this.options.fontSize!.label);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.options.colors!.secondary);
    
    const questionTitle = `${index}. ${question.title}`;
    const titleLines = this.doc.splitTextToSize(questionTitle, this.contentWidth);
    
    titleLines.forEach((line: string) => {
      this.checkPageBreak(10);
      this.doc.text(line, this.options.margins!.left, this.currentY);
      this.currentY += 5;
    });
    
    // Agregar imagen de la pregunta si existe y está habilitado
    // PERO: No agregar si es multipleChoice y las respuestas tienen imágenes (para evitar duplicados)
    const shouldAddQuestionImage = this.options.includeImages && 
                                   question.image && 
                                   question.image.trim() !== '' &&
                                   !(question.type === 'multipleChoice' && 
                                     question.responses && 
                                     question.responses.some(r => r['image'] && r['image'].trim() !== ''));
    
    if (shouldAddQuestionImage) {
      this.currentY += 2;
      await this.addImageToDocument(question.image);
    }
    
    // Respuesta según el tipo
    this.doc.setFontSize(this.options.fontSize!.value);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(this.options.colors!.text);
    
    let hasImage = shouldAddQuestionImage;
    
    switch (question.type) {
      case 'dropdown':
      case 'inputText':
      case 'longText':
        if (question.response) {
          this.addWrappedText(question.response, this.options.margins!.left + 5);
        }
        break;
        
      case 'multipleChoice':
        if (question.responses && question.responses.length > 0) {
          // Verificar si hay imágenes en las respuestas
          hasImage = hasImage || question.responses.some(r => r['image'] && r['image'].trim() !== '');
          await this.addMultipleChoiceResponses(question.responses);
        }
        break;
        
      case 'customComponents':
        if (question.components && question.components.length > 0) {
          this.addComponentsData(question.components);
        }
        break;
        
      case 'insolesPrescription':
        this.addInsolesPrescriptionData(question);
        break;
        
      default:
        if (question.response) {
          this.addWrappedText(JSON.stringify(question.response, null, 2), this.options.margins!.left + 5);
        } else if (question.responses) {
          this.addWrappedText(JSON.stringify(question.responses, null, 2), this.options.margins!.left + 5);
        }
    }
    
    // Reducir espacio después de preguntas con imágenes para aprovechar mejor el espacio
    this.currentY += hasImage ? 2 : 3;
  }

  /**
   * Agrega respuestas de tipo multipleChoice con sus imágenes
   */
  private async addMultipleChoiceResponses(responses: any[]): Promise<void> {
    for (const resp of responses) {
      this.checkPageBreak(8);
      
      // 1. Título de la respuesta
      const bulletX = this.options.margins!.left + 5;
      this.doc.text(`• ${resp.title}`, bulletX, this.currentY);
      this.currentY += 5;
      
      // 2. Imagen (si existe) - alineada con el bullet point
      let imageInfo: { x: number; width: number } | null = null;
      if (this.options.includeImages && resp.image && resp.image.trim() !== '') {
        this.currentY += 2;
        imageInfo = await this.addImageToDocument(resp.image, bulletX + 3);
        // Nota: addImageToDocument ya actualiza currentY después de agregar la imagen
      }
      
      // 3. Información adicional centrada debajo de la imagen (como "Add", "1.5mm, 3mm", etc.)
      if (resp.responses && resp.responses !== '') {
        this.doc.setFont('helvetica', 'italic');
        this.doc.setFontSize(this.options.fontSize!.value - 1); // Un poco más pequeño
        const text = resp.responses;
        const textWidth = this.doc.getTextWidth(text);
        
        // Si hay imagen, centrar el texto debajo de ella; si no, centrar en el folio
        let centeredX: number;
        if (imageInfo) {
          // Centrar debajo de la imagen
          centeredX = imageInfo.x + (imageInfo.width - textWidth) / 2;
        } else {
          // Centrar en el folio
          centeredX = this.options.margins!.left + (this.contentWidth - textWidth) / 2;
        }
        
        this.doc.text(text, centeredX, this.currentY);
        this.currentY += 6;
        this.doc.setFontSize(this.options.fontSize!.value); // Restaurar tamaño
        this.doc.setFont('helvetica', 'normal');
      }
    }
  }

  private addComponentsData(components: any[]): void {
    components.forEach((comp, index) => {
      this.checkPageBreak(20);
      
      if (comp.title) {
        this.doc.setFont('helvetica', 'bold');
        this.doc.setFontSize(this.options.fontSize!.label);
        this.doc.text(`${comp.title}:`, this.options.margins!.left + 5, this.currentY);
        this.currentY += 5;
        this.doc.setFont('helvetica', 'normal');
        this.doc.setFontSize(this.options.fontSize!.value);
      }
      
      // Extraer valores relevantes de choices (que es un objeto, no un array)
      if (comp.choices && typeof comp.choices === 'object') {
        const extractedValues = this.extractValuesFromChoicesObject(comp.choices);
        
        if (extractedValues.length > 0) {
          extractedValues.forEach(value => {
            this.checkPageBreak(8);
            const maxWidth = this.pageWidth - this.options.margins!.left - this.options.margins!.right - 10;
            const lines = this.doc.splitTextToSize(`  • ${value}`, maxWidth);
            
            lines.forEach((line: string) => {
              this.checkPageBreak(8);
              this.doc.text(line, this.options.margins!.left + 8, this.currentY);
              this.currentY += 5;
            });
          });
        }
      } else {
        // Si no hay choices, buscar otros datos relevantes
        const relevantKeys = Object.keys(comp).filter(key => 
          key !== 'title' && 
          key !== 'image' && 
          key !== 'imageSize' && 
          key !== 'imagePosition' &&
          key !== 'type' &&
          key !== 'choices' &&
          comp[key] !== undefined && 
          comp[key] !== null && 
          comp[key] !== ''
        );

        if (relevantKeys.length > 0) {
          relevantKeys.forEach(key => {
            this.checkPageBreak(8);
            const value = comp[key];
            const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            
            const maxWidth = this.pageWidth - this.options.margins!.left - this.options.margins!.right - 15;
            const text = `  ${this.formatKeyName(key)}: ${displayValue}`;
            const lines = this.doc.splitTextToSize(text, maxWidth);
            
            lines.forEach((line: string) => {
              this.checkPageBreak(8);
              this.doc.text(line, this.options.margins!.left + 8, this.currentY);
              this.currentY += 5;
            });
          });
        }
      }
      
      // Espacio entre componentes
      if (index < components.length - 1) {
        this.currentY += 2;
      }
    });
  }

  private extractValuesFromChoicesObject(choicesObj: any): string[] {
    const values: string[] = [];
    
    // Iterar sobre las propiedades del objeto choices
    Object.keys(choicesObj).forEach(key => {
      const items = choicesObj[key];
      
      // Si es un array con elementos, procesarlos
      if (Array.isArray(items) && items.length > 0) {
        items.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            // Extraer response (para dropdowns)
            if (item.response !== undefined && item.response !== null && item.response !== '') {
              const label = item.label || '';
              values.push(label ? `${label}: ${item.response}` : String(item.response));
            }
            // Extraer inputNumberValue (para number inputs)
            else if (item.inputNumberValue !== undefined && item.inputNumberValue !== null && item.inputNumberValue !== '') {
              const leftLabel = item.leftLabel || '';
              const rightLabel = item.rightLabel || '';
              const valueStr = `${item.inputNumberValue}${rightLabel}`;
              values.push(leftLabel ? `${leftLabel}: ${valueStr}` : valueStr);
            }
            // Extraer label si no hay otros valores
            else if (item.label && item.label !== '') {
              values.push(item.label);
            }
          } else if (item !== undefined && item !== null && item !== '') {
            values.push(String(item));
          }
        });
      }
    });
    
    return values;
  }

  private formatKeyName(key: string): string {
    // Convertir camelCase a palabras separadas
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private addInsolesPrescriptionData(question: Question): void {
    const fields = [
      { label: 'Length Type', value: question['lengthType'] },
      { label: 'Shell Type', value: question['shellType'] },
      { label: 'Thin Thickness', value: question['thinThickness'] }
    ];
    
    fields.forEach(field => {
      if (field.value !== undefined && field.value !== null) {
        this.checkPageBreak(8);
        this.doc.text(`${field.label}: ${field.value}`, this.options.margins!.left + 5, this.currentY);
        this.currentY += 5;
      }
    });
    
    if (question.components && question.components.length > 0) {
      this.currentY += 2;
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Components:', this.options.margins!.left + 5, this.currentY);
      this.currentY += 5;
      this.doc.setFont('helvetica', 'normal');
      
      question.components.forEach((comp) => {
        this.checkPageBreak(15);
        
        const values: string[] = [];
        
        // Tipo de componente (más legible)
        if (comp.type) {
          const typeFormatted = this.formatKeyName(comp.type);
          values.push(typeFormatted);
        }
        
        // Lado del pie
        if (comp.footSelection) {
          const footText = comp.footSelection.charAt(0).toUpperCase() + comp.footSelection.slice(1);
          values.push(`${footText} foot`);
        }
        
        // Extraer valores de left/right con toda su estructura anidada
        ['left', 'right'].forEach(side => {
          if (comp[side]) {
            const sideData = comp[side];
            const sideName = side.charAt(0).toUpperCase() + side.slice(1);
            const sideValues = this.extractNestedValues(sideData, sideName);
            
            if (sideValues.length > 0) {
              values.push(...sideValues);
            }
          }
        });
        
        // Mostrar los valores en una línea limpia
        if (values.length > 0) {
          const componentText = `  • ${values.join(' - ')}`;
          const maxWidth = this.pageWidth - this.options.margins!.left - this.options.margins!.right - 10;
          const lines = this.doc.splitTextToSize(componentText, maxWidth);
          
          lines.forEach((line: string) => {
            this.checkPageBreak(8);
            this.doc.text(line, this.options.margins!.left + 8, this.currentY);
            this.currentY += 5;
          });
        }
      });
    }
  }

  private extractNestedValues(obj: any, prefix: string = '', parentKey: string = ''): string[] {
    const values: string[] = [];
    
    if (!obj || typeof obj !== 'object') {
      return values;
    }
    
    // Si tiene value directo
    if (obj.value !== undefined && obj.value !== null && obj.value !== '') {
      const formattedValue = this.formatValueWithUnit(obj.value);
      values.push(prefix ? `${prefix}: ${formattedValue}` : formattedValue);
      return values;
    }
    
    // Si tiene selectedName directo
    if (obj.selectedName !== undefined && obj.selectedName !== null && obj.selectedName !== '') {
      const formattedValue = this.formatValueWithUnit(obj.selectedName);
      values.push(prefix ? `${prefix}: ${formattedValue}` : formattedValue);
      return values;
    }
    
    // Propiedades especiales que agrupan subpropiedades (medial, lateral)
    const groupingProps = ['medial', 'lateral'];
    const hasGroupingProps = groupingProps.some(prop => obj[prop] !== undefined && obj[prop] !== null);
    
    if (hasGroupingProps) {
      // Procesar cada grupo (medial/lateral) por separado
      groupingProps.forEach(groupProp => {
        if (obj[groupProp] !== undefined && obj[groupProp] !== null) {
          const groupValue = obj[groupProp];
          const groupName = this.formatKeyName(groupProp);
          
          if (typeof groupValue === 'object' && !Array.isArray(groupValue)) {
            // Extraer subpropiedades del grupo
            const subValues: string[] = [];
            
            Object.keys(groupValue).forEach(subKey => {
              if (groupValue[subKey] !== undefined && groupValue[subKey] !== null && groupValue[subKey] !== '' &&
                  subKey !== 'type' && subKey !== 'selectedName') {
                
                if (typeof groupValue[subKey] === 'object') {
                  const deepValues = this.extractNestedValues(groupValue[subKey], this.formatKeyName(subKey), groupProp);
                  subValues.push(...deepValues);
                } else {
                  const formattedValue = this.formatValueWithUnit(groupValue[subKey]);
                  subValues.push(`${this.formatKeyName(subKey)}: ${formattedValue}`);
                }
              }
            });
            
            if (subValues.length > 0) {
              values.push(`${groupName} (${subValues.join(', ')})`);
            }
          }
        }
      });
      
      return values;
    }
    
    // Buscar otras propiedades anidadas
    const nestedProps = ['width', 'height', 'type', 'rigidity', 'reversed'];
    
    nestedProps.forEach(prop => {
      if (obj[prop] !== undefined && obj[prop] !== null) {
        const propValue = obj[prop];
        
        // Si es un objeto, extraer recursivamente
        if (typeof propValue === 'object' && !Array.isArray(propValue)) {
          const propName = this.formatKeyName(prop);
          const nestedValues = this.extractNestedValues(propValue, propName, prop);
          values.push(...nestedValues);
        } else if (typeof propValue !== 'object') {
          // Si es un valor simple
          const formattedValue = this.formatValueWithUnit(propValue);
          values.push(`${this.formatKeyName(prop)}: ${formattedValue}`);
        }
      }
    });
    
    // Si no encontramos nada con las propiedades conocidas, buscar todas las propiedades
    if (values.length === 0) {
      Object.keys(obj).forEach(key => {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '' && 
            key !== 'type' && key !== 'selectedName' && key !== 'footSelection') {
          if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
            const keyName = this.formatKeyName(key);
            const nestedValues = this.extractNestedValues(obj[key], keyName, key);
            values.push(...nestedValues);
          } else if (typeof obj[key] !== 'object') {
            const formattedValue = this.formatValueWithUnit(obj[key]);
            values.push(`${this.formatKeyName(key)}: ${formattedValue}`);
          }
        }
      });
    }
    
    return values;
  }

  private formatValueWithUnit(value: any): string {
    if (typeof value === 'number') {
      return `${value}mm`;
    }
    
    // Si es un string que representa un número
    if (typeof value === 'string') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && value.trim() === numValue.toString()) {
        return `${numValue}mm`;
      }
    }
    
    return String(value);
  }

  /**
   * Agrega una imagen al documento PDF de forma asíncrona
   */
  /**
   * Agrega una imagen al documento PDF de forma asíncrona
   * @param imageUrl URL de la imagen
   * @param xPosition Posición X opcional (si no se proporciona, se alinea a la izquierda con margen)
   * @returns Objeto con la posición X y ancho de la imagen, o null si no se pudo cargar
   */
  private async addImageToDocument(imageUrl: string, xPosition?: number): Promise<{ x: number; width: number } | null> {
    if (!imageUrl || imageUrl.trim() === '') {
      return null;
    }

    console.log('🖼️ Loading image:', imageUrl.substring(imageUrl.lastIndexOf('/') + 1));

    try {
      const img = await this.loadImage(imageUrl);
      
      // Validar que la imagen se cargó correctamente
      if (!img.width || !img.height || img.width === 0 || img.height === 0) {
        console.error('❌ Invalid image dimensions:', img.width, 'x', img.height);
        return null;
      }
      
      // Calcular dimensiones manteniendo aspect ratio
      const maxWidth = this.contentWidth * 0.3; // 30% del ancho disponible (más pequeño)
      const maxHeight = 40; // Altura máxima en mm (más pequeño)
      
      let imgWidth = img.width * 0.264583; // Convertir px a mm (1px = 0.264583mm)
      let imgHeight = img.height * 0.264583;
      
      // Escalar si es necesario
      if (imgWidth > maxWidth) {
        const ratio = maxWidth / imgWidth;
        imgWidth = maxWidth;
        imgHeight = imgHeight * ratio;
      }
      
      if (imgHeight > maxHeight) {
        const ratio = maxHeight / imgHeight;
        imgHeight = maxHeight;
        imgWidth = imgWidth * ratio;
      }
      
      // Verificar si hay espacio suficiente en la página
      this.checkPageBreak(imgHeight + 10);
      
      // Usar la posición X proporcionada o alinear a la izquierda con un pequeño margen
      const finalXPosition = xPosition !== undefined ? xPosition : this.options.margins!.left + 5;
      
      // Determinar el formato de la imagen
      const format = this.getImageFormat(imageUrl);
      
      // Agregar la imagen al PDF
      this.doc.addImage(
        img,
        format,
        finalXPosition,
        this.currentY,
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );
      
      console.log('✅ Image added to PDF');
      
      this.currentY += imgHeight + 5;
      
      // Devolver información sobre la imagen
      return { x: finalXPosition, width: imgWidth };
    } catch (error) {
      console.error('❌ Error adding image to PDF:', error);
      // Continuar sin la imagen
      return null;
    }
  }


  /**
   * Determina el formato de la imagen basándose en la URL
   */
  private getImageFormat(url: string): string {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('.png') || lowerUrl.includes('png')) {
      return 'PNG';
    } else if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('jpeg')) {
      return 'JPEG';
    } else if (lowerUrl.includes('.gif')) {
      return 'GIF';
    }
    // Por defecto, intentar JPEG
    return 'JPEG';
  }

  /**
   * Carga una imagen de forma asíncrona
   */
  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise(async (resolve, reject) => {
      try {
        // Intentar cargar la imagen usando fetch para evitar problemas de CORS
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        const img = new Image();
        
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };
        
        img.onerror = (error) => {
          URL.revokeObjectURL(objectUrl);
          console.error(`❌ Failed to load image from blob:`, error);
          reject(new Error(`Failed to load image: ${url}`));
        };
        
        img.src = objectUrl;
      } catch (error) {
        console.error(`❌ Failed to fetch image:`, error);
        
        // Fallback: intentar cargar directamente
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        
        img.onload = () => {
          resolve(img);
        };
        
        img.onerror = (error) => {
          console.error(`❌ Failed to load image directly:`, error);
          reject(new Error(`Failed to load image: ${url}`));
        };
        
        // Agregar timestamp para evitar problemas de caché
        const separator = url.includes('?') ? '&' : '?';
        const finalUrl = `${url}${separator}_t=${Date.now()}`;
        img.src = finalUrl;
      }
    });
  }

  private addSectionHeader(title: string): void {
    this.doc.setFontSize(this.options.fontSize!.sectionHeader);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(this.options.colors!.primary);
    
    this.doc.text(title, this.options.margins!.left, this.currentY);
    this.currentY += 7;
    
    // Línea debajo del título
    this.doc.setDrawColor(this.options.colors!.border);
    this.doc.setLineWidth(0.3);
    this.doc.line(
      this.options.margins!.left,
      this.currentY,
      this.pageWidth - this.options.margins!.right,
      this.currentY
    );
    this.currentY += 8; // Aumentado de 5 a 8 para más espacio
  }

  private addWrappedText(text: string, xPosition: number): void {
    const maxWidth = this.pageWidth - xPosition - this.options.margins!.right;
    const lines = this.doc.splitTextToSize(text, maxWidth);
    
    lines.forEach((line: string) => {
      this.checkPageBreak(8);
      this.doc.text(line, xPosition, this.currentY);
      this.currentY += 5;
    });
  }

  private checkPageBreak(requiredSpace: number): void {
    if (this.currentY + requiredSpace > this.pageHeight - this.options.margins!.bottom) {
      this.doc.addPage();
      this.currentY = this.options.margins!.top;
    }
  }

  private formatDate(date: Date | string): string {
    if (!date) {
      return new Date().toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
    
    const d = typeof date === 'string' ? new Date(date) : date;
    
    // Verificar si la fecha es válida
    if (isNaN(d.getTime())) {
      // Si la fecha es inválida, usar la fecha actual
      return new Date().toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
    
    return d.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }
}
